/**
 * The Marking Panel — the final reporting screen's bars.
 *
 * Authority: `05-reporting-marking.md` §B2/§B3/§B3a and `05a-marking-criteria-reference.md`
 * (which wins wherever §B disagrees). Visual language: the Reporting Section of
 * `design-drafts/official-profile-detail-v1.html`, in the editorial-v2 vocabulary.
 *
 * Four rules this component exists to keep:
 *
 *  1. **The set is the official's own.** The criteria arrive from the API, which derives
 *     them from the roster role. Nothing here filters, reorders or normalises them — the
 *     zero-weight `Video Umpire` row stays in, and drops out of the outcome by arithmetic.
 *
 *  2. **The scale is 0–100 in tens.** The input is `min=0 max=100 step=10`, and the handler
 *     snaps anything else onto the scale rather than sending it, so a synthetic or
 *     mistyped value cannot reach a route that would rightly refuse it.
 *
 *  3. **The outcome recomputes live.** `computePerformanceOutcome` from `@hockeytd/shared`
 *     — the same function the API uses — gives the figure the instant a bar moves. That
 *     figure is provisional: **the server's reply to the write is authoritative** and
 *     replaces the whole panel, so the screen can never drift from the stored marking.
 *
 *  4. **A failed write is visible where it happened.** Each change is optimistic; on
 *     refusal the panel reverts to exactly the state it had and the row says why. Nothing
 *     retries silently — a bar that looks saved must be saved.
 */

import { useState } from "react";

import { buildMarksPayload, marksTotalsRow, tidyCriterionDisplayName, type MarkingCriterion, type TmsMarksPayload } from "@hockeytd/shared";

import { ApiError, putMark, pushMarks } from "../../api/client.js";
import type { MarkingPanelBody, MarksPushBody, PushReceipt } from "../../api/types.js";
import { MarkingHeader } from "./MarkingHeader.js";
import { PushConfirm } from "./PushConfirm.js";
import { PushFallbacks } from "./PushFallbacks.js";
import { PushReceiptNote, pushTime } from "./PushReceiptNote.js";
import {
  liveOutcome,
  liveWeightTotal,
  markFor,
  withMark,
} from "./outcome.js";

/** The scale, restated so the input and the snapping agree by construction (§B3). */
const MARK_MIN = 0;
const MARK_MAX = 100;
const MARK_STEP = 10;

/**
 * How one criterion is written. `putMark` is the only implementation; the indirection
 * exists so a component test can drive the panel without a network, not so the transport
 * can be swapped at runtime.
 */
export type MarkWriter = (
  tmsPersonId: string,
  criterionStableKey: string,
  body: { value: number | null } | { na: true },
) => Promise<MarkingPanelBody>;

/** The W2 press. `pushMarks` is the only implementation; the seam exists for the tests. */
export type MarksPusher = (tmsPersonId: string) => Promise<MarksPushBody>;

export interface MarkingPanelProps {
  /** The official's display name for the marking header strip. */
  officialName: string;
  /**
   * The panel as fetched. Held as seed state, which is safe because the marking screen
   * mounts this component under `key={tmsPersonId}` — a different official is a different
   * component instance, never a mutated one.
   */
  initialPanel: MarkingPanelBody;
  /** Injected in tests only; the real write is `putMark`. */
  write?: MarkWriter;
  /** Injected in tests only; the real press is `pushMarks`. */
  push?: MarksPusher;
  /**
   * **Whether the CALLER may push this marking**, defaulting to the server's `pushableByTd`.
   *
   * The panel is one component and the marking is one record (§B3b), but the two surfaces
   * that draw it have opposite push rights on an umpire: the Control Centre reads
   * `pushableByTd`, which is false there, and the UM app's own marking screen passes `true`,
   * because that route serves the owning manager her own umpires and nobody else's. The
   * ownership decision itself is the API's — this prop only says which of its answers applies
   * to the person looking at the screen.
   */
  pushable?: boolean;
  /**
   * What to say where the push control would be when `pushable` is false. Defaults to §B3b's
   * sentence for the TD. A control-shaped hole with no explanation reads as a bug.
   */
  notPushableNote?: React.ReactNode;
}

/** §B3b, as the Technical Delegate's marking screen states it. */
const TD_CANNOT_PUSH_NOTE =
  "The owning Umpires Manager pushes these marks to TMS with the umpire’s report. " +
  "Agree them here.";

/**
 * The payload a press would send, built with the same builder the API builds it with — so
 * the confirmation dialogue, the clipboard, the Word download and the receipt all describe
 * one marking. The server rebuilds it from the stored marks and does not trust this one;
 * what this guarantees is that the TD confirmed what the server then sent.
 */
export function panelPayload(panel: MarkingPanelBody, officialName: string): TmsMarksPayload {
  return buildMarksPayload({
    subjectTmsPersonId: panel.tmsPersonId,
    subjectName: officialName,
    tournamentId: panel.tournamentId,
    gradingRoleKey: panel.gradingRoleKey,
    criteria: panel.criteria,
    marks: panel.marks.map((mark) => ({
      criterion_stable_key: mark.criterionStableKey,
      value: mark.value,
      na: mark.na,
    })),
  });
}

/** Snap any incoming number onto the 0–100-in-tens scale. */
export function snapToScale(raw: number): number {
  if (!Number.isFinite(raw)) return MARK_MIN;
  const stepped = Math.round(raw / MARK_STEP) * MARK_STEP;
  return Math.min(MARK_MAX, Math.max(MARK_MIN, stepped));
}

export function MarkingPanel({
  officialName,
  initialPanel,
  write = putMark,
  push = pushMarks,
  pushable = initialPanel.pushableByTd,
  notPushableNote = TD_CANNOT_PUSH_NOTE,
}: MarkingPanelProps): React.JSX.Element {
  const [panel, setPanel] = useState<MarkingPanelBody>(initialPanel);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ key: string; message: string } | null>(null);
  /** Whether the confirmation dialogue is open. A press is never one click (gate 4). */
  const [confirming, setConfirming] = useState<boolean>(false);
  const [pushing, setPushing] = useState<boolean>(false);
  /**
   * The last attempt, of either outcome. Seeded from the server so a receipt survives a
   * reload — §5.1 wants a persistent record, not a toast — and replaced by each press.
   */
  const [receipt, setReceipt] = useState<PushReceipt | null>(initialPanel.lastReceipt);

  const { criteria, marks } = panel;
  const isUmpireSet = panel.gradingRoleKey === "UMPIRE";

  async function commit(
    criterionStableKey: string,
    next: { value: number | null; na: boolean },
  ): Promise<void> {
    const previous = panel;
    const nextMarks = withMark(previous.marks, criterionStableKey, next);

    // Optimistic: the figure moves with the bar. Both live figures are recomputed with the
    // shared formula, never patched by hand.
    setPanel({
      ...previous,
      marks: nextMarks,
      outcome: liveOutcome(previous.criteria, nextMarks),
      liveWeightTotal: liveWeightTotal(previous.criteria, nextMarks),
    });
    setSavingKey(criterionStableKey);
    setRowError(null);

    try {
      const authoritative = await write(
        previous.tmsPersonId,
        criterionStableKey,
        next.na ? { na: true } : { value: next.value },
      );
      // The server's panel wins outright — outcome, live Σweight and every other mark.
      setPanel(authoritative);
    } catch (error) {
      setPanel(previous);
      setRowError({
        key: criterionStableKey,
        message:
          error instanceof ApiError
            ? error.message
            : "The mark was not saved. The bar has been put back.",
      });
    } finally {
      setSavingKey(null);
    }
  }

  /**
   * The press. The dialogue has already shown the payload; this sends nothing but the
   * instruction, because the marks the server pushes are the marks it has stored.
   *
   * A refusal is **shown, not retried**, and the panel is left exactly as it was: the whole
   * point of the receipt is that a push which did not land looks like one that did not land.
   */
  async function commitPush(): Promise<void> {
    setPushing(true);
    try {
      const result = await push(panel.tmsPersonId);
      setPanel(result.panel);
      setReceipt(result.receipt);
      setConfirming(false);
    } catch (error) {
      // The API records the attempt whether or not it landed, so the panel is re-read to
      // pick the stored receipt up; if even that fails, the error itself is shown.
      setReceipt({
        id: "unsent",
        // The path follows ownership, not the surface: an umpire's marks are W3 wherever the
        // press happened, and `pushableByTd` is the server's own statement of which it is.
        path: panel.pushableByTd ? "W2" : "W3",
        kind: "marks",
        subjectTmsPersonId: panel.tmsPersonId,
        tournamentId: panel.tournamentId,
        pressedByTmsPersonId: "",
        // This receipt was never recorded — the request did not reach the API — so there is
        // no presser to name. Empty rather than the caller's own guess at who they are.
        pressedByMediaName: "",
        pressedAt: new Date().toISOString(),
        outcome: "failed",
        failureReason: error instanceof ApiError ? error.code.replace(/^tms_/, "") : null,
        detail:
          error instanceof ApiError
            ? error.message
            : "The marks were not pushed. Nothing reached TMS.",
        payloadSummary: payload.marks.length + " category marks, not sent",
        payload,
        tmsReference: null,
        transport: "unknown",
      });
      setConfirming(false);
    } finally {
      setPushing(false);
    }
  }

  const outcome = panel.outcome;
  const payload = panelPayload(panel, officialName);
  /**
   * The row that closes the table, worded by `@hockeytd/shared` so the screen, the Word
   * download and the clipboard cannot say it three different ways. It is read off the
   * payload rather than off `panel`, because the payload is rebuilt from the criteria and
   * the marks on every render and therefore tracks an optimistic change, whereas the
   * server's own `unmarkedCriteria` is a moment behind until the write lands.
   */
  const totals = marksTotalsRow(payload);
  const incomplete = panel.unmarkedCriteria.length > 0;
  /**
   * A mark is in flight, so the panel is showing an optimistic figure the server has not
   * confirmed (and might yet refuse).
   *
   * `POST /officials/:tmsPersonId/marks/push` carries no body — it pushes the marks the
   * server has STORED — so the dialogue must not itemise a figure that is not stored yet.
   * There is no unsaved-draft state on this panel to warn about, as there is in the report
   * composer: every bar writes as it is released. There is only this window, and the honest
   * handling of a window that closes by itself in a moment is to wait for it, not to
   * annotate it. So the press is withheld while it is open, and a dialogue already up is
   * blocked until the write lands.
   */
  const saving = savingKey !== null;

  return (
    <section className="panel panel--d1" aria-labelledby="markTitle" data-howto="marking-panel">
      <div className="panel__head">
        <h3 className="panel__title" id="markTitle">
          Marking Panel
        </h3>
        <span className="panel__note">Given Once, At The End Of The Tournament</span>
        <span className="spacer" />

        {/* §B2's chip: it appears only once a push has actually been ACCEPTED. */}
        {panel.lastPushedAt === null ? null : (
          <span className="pushchip" data-testid="marks-pushed">
            <span aria-hidden="true">✓</span>Marks Pushed {pushTime(panel.lastPushedAt)}
          </span>
        )}
        {panel.editedSinceLastPush ? (
          <span
            className="editchip"
            title="These marks have moved since the last push. TMS still holds the earlier set until they are pushed again."
            data-testid="marks-edited-since-push"
          >
            Edited Since Last Push
          </span>
        ) : null}

        {pushable ? (
          <button
            type="button"
            className="gbtn gbtn--solid"
            data-testid="push-marks"
            data-howto="push-marks"
            disabled={pushing || confirming || incomplete || saving}
            onClick={() => {
              setConfirming(true);
            }}
          >
            Push Marks To TMS
          </button>
        ) : (
          // Ruling R2′/§B3b: an umpire's marks are agreed with the owning UM and pushed by
          // her, with the report (W3). The screen names who does it rather than leaving a hole.
          <span className="btnhint" data-testid="marks-owned-by-um">
            {notPushableNote}
          </span>
        )}
      </div>

      {pushable ? (
        <div className="pushbar">
          <span className="btnhint" data-testid="push-marks-hint">
            Pushed As Draft — TD Publishes In TMS
          </span>
          {saving ? (
            <span className="btnhint" data-testid="push-marks-saving">
              A mark is still being saved. The push sends the marks as stored, so it waits for
              this one to land.
            </span>
          ) : null}
          {incomplete ? (
            <span className="btnhint" data-testid="push-marks-incomplete">
              {panel.unmarkedCriteria.length} criteria carry no mark:{" "}
              {panel.unmarkedCriteria.join(", ")}. Mark each one, or set it N/A, before
              pushing the set.
            </span>
          ) : null}
        </div>
      ) : null}

      {confirming ? (
        <PushConfirm
          payload={payload}
          officialName={officialName}
          confirmLabel="Push As Draft"
          busy={pushing}
          {...(incomplete
            ? {
                blocker: `${panel.unmarkedCriteria.join(", ")} carry no mark yet.`,
              }
            : saving
              ? {
                  blocker:
                    "A mark is still being saved. The push sends the marks as stored, so it " +
                    "waits for this one to land.",
                }
              : {})}
          onConfirm={() => void commitPush()}
          onCancel={() => {
            setConfirming(false);
          }}
        />
      ) : null}

      {receipt === null ? null : <PushReceiptNote receipt={receipt} />}

      <MarkingHeader
        officialName={officialName}
        role={panel.role}
        outcome={outcome}
        dateOfBirth={panel.dateOfBirth}
        gradingLevel={panel.gradingLevel}
      />

      <div>
        {criteria.map((criterion) => (
          <CriterionRow
            key={criterion.criterion_stable_key}
            criterion={criterion}
            mark={markFor(marks, criterion.criterion_stable_key)}
            saving={savingKey === criterion.criterion_stable_key}
            error={rowError?.key === criterion.criterion_stable_key ? rowError.message : null}
            onMark={(value) => void commit(criterion.criterion_stable_key, { value, na: false })}
            onNa={(na) =>
              void commit(criterion.criterion_stable_key, { value: null, na })
            }
          />
        ))}

        {/* The totals row, in the same three columns as the criteria above it. */}
        <div
          className="critrow critrow--total"
          data-testid="critrow-total"
          data-howto="totals-row"
        >
          <span className="critname">
            <b>{totals.label}</b>
          </span>
          <span className="crittotalweight" data-testid="live-weight-total">
            {totals.weightText}
          </span>
          <span className="crittotalmark" data-testid="totals-mark">
            <b>{totals.markValue}</b>
            {totals.unmarkedNote === null ? null : (
              <span className="crittotalnote"> · {totals.unmarkedNote}</span>
            )}
          </span>
        </div>
      </div>

      <div className="panel__foot">
        <b>Weights total {panel.weightTotal} —</b> the Performance Outcome is the weighted mean
        of the marks, recomputed the moment you move any bar. Marks step in tens from 0 to 100.
        <div className="hidenote">
          <b>Weight in play</b> is how much of the set is still counting towards the mark.
          Setting a criterion to <b>N/A</b> takes its weight out of that figure. A criterion
          you have simply not marked yet keeps its weight, and is counted beside the mark
          instead, because it is an omission rather than a decision.
        </div>
        {isUmpireSet ? (
          <div className="hidenote">
            <b>Video Umpire is recorded, not weighted —</b> the mark is kept and printed on the
            report, but it carries weight 0 and does not move the Performance Outcome.
          </div>
        ) : null}

        {/* §4: the fallbacks ship BESIDE the push, not after it fails. They render from
            what is on screen, so they work when TMS cannot be reached at all. */}
        <PushFallbacks payload={payload} />
      </div>
    </section>
  );
}

interface CriterionRowProps {
  criterion: MarkingCriterion;
  mark: { value: number | null; na: boolean } | undefined;
  saving: boolean;
  error: string | null;
  onMark: (value: number) => void;
  onNa: (na: boolean) => void;
}

function CriterionRow({
  criterion,
  mark,
  saving,
  error,
  onMark,
  onNa,
}: CriterionRowProps): React.JSX.Element {
  const na = mark?.na === true;
  const value = mark?.value ?? null;
  const unmarked = !na && value === null;
  // A zero-weight criterion needs no N/A — it is already outside the calculation (§B3).
  const naPermitted = criterion.weight > 0;

  const classes = [
    "critrow",
    na ? "is-na" : "",
    saving ? "is-saving" : "",
    error !== null ? "has-error" : "",
  ]
    .filter((c) => c !== "")
    .join(" ");

  return (
    <div className={classes} data-testid={`critrow-${criterion.criterion_stable_key}`}>
      <span className="critname">
        {tidyCriterionDisplayName(criterion.name)}
        {criterion.weight === 0 ? (
          <span className="critnote">Recorded on the report · excluded from the outcome</span>
        ) : null}
      </span>

      <span className="critwt num" title="Weight">
        {criterion.weight}
      </span>

      <span className="slidewrap">
        <input
          type="range"
          min={MARK_MIN}
          max={MARK_MAX}
          step={MARK_STEP}
          value={value ?? MARK_MIN}
          disabled={na}
          aria-label={`${tidyCriterionDisplayName(criterion.name)} mark`}
          {...(unmarked ? { "aria-valuetext": "Not marked" } : {})}
          onChange={(event) => {
            onMark(snapToScale(Number(event.target.value)));
          }}
        />
      </span>

      <span
        className={`critmark${unmarked || na ? " is-unset" : ""}`}
        data-testid={`critmark-${criterion.criterion_stable_key}`}
      >
        {na ? "N/A" : unmarked ? "—" : value}
        {na ? null : <span className="of">/100</span>}
      </span>

      {naPermitted ? (
        <label className={`natoggle${na ? " is-on" : ""}`}>
          <input
            type="checkbox"
            checked={na}
            aria-label={`${tidyCriterionDisplayName(criterion.name)} not applicable`}
            onChange={(event) => {
              onNa(event.target.checked);
            }}
          />
          N/A
        </label>
      ) : (
        <span className="zeroweight" title="Weight 0 — outside the calculation">
          Wt 0
        </span>
      )}

      {error !== null ? (
        <span className="rowerror" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
